
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import type { ImportDataType, CSVRowData } from "../types";

interface ImportPreviewProps {
  data: CSVRowData[];
  mappedFields: Record<string, string>;
  dataType: ImportDataType;
  onBack: () => void;
  onConfirm: () => Promise<void>;
  isImporting: boolean;
}

export function ImportPreview({ 
  data, 
  mappedFields, 
  dataType, 
  onBack, 
  onConfirm,
  isImporting
}: ImportPreviewProps) {
  const [previewData, setPreviewData] = useState<Record<string, string>[]>([]);
  const [displayFields, setDisplayFields] = useState<string[]>([]);
  
  // Transform data for preview based on field mappings
  useEffect(() => {
    if (!data.length) return;
    
    // Get fields that are mapped
    const activeFields = Object.entries(mappedFields)
      .filter(([_, csvHeader]) => csvHeader && csvHeader !== "ignore")
      .map(([dbField, _]) => dbField);
      
    setDisplayFields(activeFields);
    
    // Transform data based on mapping
    const transformed = data.slice(0, 5).map(row => {
      const newRow: Record<string, string> = {};
      
      Object.entries(mappedFields).forEach(([dbField, csvHeader]) => {
        if (csvHeader && csvHeader !== "ignore") {
          newRow[dbField] = String(row[csvHeader] || "");
        }
      });
      
      return newRow;
    });
    
    setPreviewData(transformed);
  }, [data, mappedFields]);
  
  const handleCopyToClipboard = () => {
    // Create CSV content
    const headers = displayFields.join(',');
    const rows = previewData.map(row => 
      displayFields.map(field => `"${row[field] || ""}"`).join(',')
    ).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    
    navigator.clipboard.writeText(csvContent)
      .then(() => toast.success("Preview data copied to clipboard"))
      .catch(err => toast.error("Failed to copy: " + err));
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Preview Data (First 5 rows)</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleCopyToClipboard}
          className="text-xs"
        >
          Copy to Clipboard
        </Button>
      </div>
      
      <div className="border rounded-md overflow-auto max-h-96">
        <Table>
          <TableHeader>
            <TableRow>
              {displayFields.map(field => (
                <TableHead key={field} className="whitespace-nowrap">
                  {field.replace(/_/g, ' ')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {previewData.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {displayFields.map(field => (
                  <TableCell key={field} className="whitespace-nowrap">
                    {row[field] || "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {data.length > 5 ? `Showing 5 of ${data.length} rows` : `Showing all ${data.length} rows`}
        </p>
        <p className="text-sm text-muted-foreground">
          Please review the data above to ensure it looks correct before proceeding with the import.
        </p>
      </div>
      
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          Back to Field Mapping
        </Button>
        <Button 
          onClick={onConfirm} 
          disabled={isImporting || previewData.length === 0}
        >
          {isImporting ? "Importing..." : `Import ${data.length} ${dataType}`}
        </Button>
      </div>
    </div>
  );
}
